<template>
  <v-card class="pa-3 mt-3">
    <div class="layout row wrap align-center mb-2">
      <h2 class="title mb-0">לוג שינויים</h2>
      <v-spacer />
      <v-btn
        icon
        flat
        :loading="loading"
        @click="loadEntries"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
    </div>
    <p class="body-1 grey--text mb-3">
      שינויים שבוצעו אוטומטית מתשובות בצ'אט בדיקה (AI). ניתן לבטל או לתקן ערך.
    </p>
    <v-progress-linear
      v-if="loading && !entries.length"
      indeterminate
      height="2"
      class="mb-2"
    />
    <v-alert
      v-else-if="!entries.length"
      :value="true"
      color="grey lighten-4"
      class="grey--text"
    >
      אין רשומות שינוי עדיין
    </v-alert>
    <v-expansion-panel
      v-else
      class="elevation-0"
    >
      <v-expansion-panel-content
        v-for="entry in entries"
        :key="entry._id"
      >
        <template v-slot:header>
          <div class="layout column fill-height">
            <span class="subheading font-weight-medium">
              {{ formatEntryDate(entry.created) }}
              <v-chip
                v-if="entry.channel"
                small
                outline
                class="mr-2"
              >
                {{ channelLabel(entry.channel) }}
              </v-chip>
              <v-chip
                v-if="entry.rolledBack"
                small
                color="grey"
                text-color="white"
              >
                בוטל
              </v-chip>
            </span>
            <span
              v-if="entry.summary"
              class="body-1 grey--text text--darken-1 mt-1"
            >
              {{ entry.summary }}
            </span>
          </div>
        </template>
        <div class="pt-2">
          <p
            v-if="entry.replyPreview"
            class="body-1 grey--text mb-3"
          >
            <span class="font-weight-bold">תשובת ספק:</span>
            {{ entry.replyPreview }}
          </p>
          <div
            v-for="(change, idx) in visibleChanges(entry)"
            :key="idx"
            class="mb-3"
          >
            <div class="caption grey--text">{{ change.label || change.type }}</div>
            <div
              dir="ltr"
              class="text-sm-right"
            >
              <span
                v-if="change.beforeValue"
                class="grey--text"
              >{{ change.beforeValue }}</span>
              <span
                v-if="change.beforeValue"
                class="mx-1"
              >←</span>
              <span class="font-weight-medium">{{ formatAfterValue(change) }}</span>
            </div>
            <div
              v-if="canEditChange(entry, change)"
              class="mt-1"
            >
              <v-btn
                small
                flat
                color="primary"
                @click="openEdit(entry, idx, change)"
              >
                שינוי ערך
              </v-btn>
            </div>
          </div>
          <v-btn
            v-if="!entry.rolledBack && hasRollbackableChanges(entry)"
            color="warning"
            flat
            small
            :loading="rollingBackId === entry._id"
            @click="confirmRollback(entry)"
          >
            ביטול כל השינויים ברשומה זו
          </v-btn>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-dialog
      v-model="editDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title class="headline">עדכון ערך</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ editChangeLabel }}</p>
          <v-text-field
            v-model="editValue"
            label="ערך חדש"
            dir="ltr"
            class="text-sm-right"
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            @click="editDialog = false"
          >
            ביטול
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingEdit"
            :disabled="!editValue.trim()"
            @click="saveEdit"
          >
            שמירה
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { format } from "date-fns";
import { getApi } from "@/api";
const EDITABLE_TYPES = ["contact_name", "email", "phone"];

export default {
  props: {
    partnerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      entries: [],
      loading: false,
      rollingBackId: null,
      editDialog: false,
      editValue: "",
      editChangeLabel: "",
      editEntry: null,
      editChangeIndex: null,
      savingEdit: false
    };
  },
  mounted() {
    this.loadEntries();
  },
  methods: {
    formatEntryDate(created) {
      if (!created) return "";
      return format(new Date(created), "DD/MM/YYYY HH:mm");
    },
    channelLabel(channel) {
      if (channel === "whatsapp") return "WhatsApp";
      if (channel === "email") return "אימייל";
      return channel;
    },
    visibleChanges(entry) {
      return (entry.changes || []).filter(
        change => change.type !== "researcher_notes_ai"
      );
    },
    formatAfterValue(change) {
      if (change.type === "last_validation" && change.afterValue) {
        const d = new Date(change.afterValue);
        if (!Number.isNaN(d.getTime())) {
          return format(d, "DD/MM/YYYY");
        }
      }
      return change.afterValue || "—";
    },
    hasRollbackableChanges(entry) {
      return (entry.changes || []).some(
        c => c.type !== "researcher_notes_ai" || entry.changes.length === 1
      );
    },
    canEditChange(entry, change) {
      if (entry.rolledBack) return false;
      if (!EDITABLE_TYPES.includes(change.type)) return false;
      if (change.type === "researcher_notes_ai") return false;
      return true;
    },
    realChangeIndex(entry, change) {
      return (entry.changes || []).indexOf(change);
    },
    async loadEntries() {
      if (!this.partnerId) return;
      this.loading = true;
      try {
        const api = await getApi();
        const { data } = await api.get(
          `/partners/${this.partnerId}/check-change-log`,
          { params: { limit: 100 } }
        );
        this.entries = (data && data.results) || [];
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    async refreshPartner() {
      await this.$store.dispatch("API_GET", {
        url: "/partners/" + this.partnerId,
        entityType: "partners",
        schema: "partnerListSchema"
      });
    },
    confirmRollback(entry) {
      const ok = window.confirm(
        "לבטל את כל השינויים שבוצעו ברשומה זו? פרטי הספק יוחזרו למצב שלפני העדכון."
      );
      if (ok) {
        this.rollback(entry);
      }
    },
    async rollback(entry) {
      this.rollingBackId = entry._id;
      try {
        const api = await getApi();
        await api.post(
          `/partners/${this.partnerId}/check-change-log/${entry._id}/rollback`
        );
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "השינויים בוטלו",
          color: "success"
        });
        await this.loadEntries();
        await this.refreshPartner();
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.rollingBackId = null;
      }
    },
    openEdit(entry, visibleIdx, change) {
      const idx = this.realChangeIndex(entry, change);
      this.editEntry = entry;
      this.editChangeIndex = idx;
      this.editChangeLabel = change.label || change.type;
      this.editValue = change.afterValue || "";
      this.editDialog = true;
    },
    async saveEdit() {
      if (!this.editEntry || this.editChangeIndex == null) return;
      this.savingEdit = true;
      try {
        const api = await getApi();
        await api.patch(
          `/partners/${this.partnerId}/check-change-log/${this.editEntry._id}/changes/${this.editChangeIndex}`,
          { value: this.editValue.trim() }
        );
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "הערך עודכן",
          color: "success"
        });
        this.editDialog = false;
        await this.loadEntries();
        await this.refreshPartner();
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.savingEdit = false;
      }
    },
    onChangeLogSocket(payload) {
      if (!payload || payload.partnerId !== this.partnerId) return;
      this.loadEntries();
      this.refreshPartner();
    }
  },
  sockets: {
    partner_check_change_log(payload) {
      this.onChangeLogSocket(payload);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/partners/partner/PartnerCheckChangeLogCard.vue